import React from "react";

import { formatConfidencePercent } from "../utils/formatters";

interface ConfidenceBadgeProps {
  confidence: number | null;
  /** When provided, renders as a clickable button (used for confidence explainer toggle). */
  onClick?: () => void;
  /** Whether the explainer panel is currently open. Controls aria-expanded. */
  expanded?: boolean;
}

export type ConfidenceTier = "elite" | "strong" | "solid" | "lean" | "tossup" | "unavailable";

export function getConfidenceTier(confidence: number | null): ConfidenceTier {
  if (confidence === null || Number.isNaN(confidence)) {
    return "unavailable";
  }
  if (confidence >= 0.70) {
    return "elite";
  }
  if (confidence >= 0.63) {
    return "strong";
  }
  if (confidence >= 0.58) {
    return "solid";
  }
  if (confidence >= 0.53) {
    return "lean";
  }
  return "tossup";
}

/* SVG gauge ring constants */
const RING_SIZE = 68;
const RING_RADIUS = 26;
const RING_STROKE = 4;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
const CENTER = RING_SIZE / 2;

/** Color pairs per tier: [main, highlight] */
export const TIER_COLORS: Record<string, [string, string]> = {
  elite:       ["#00c07a", "#3dd68c"],
  strong:      ["#36d3a2", "#6ee7c0"],
  solid:       ["#38bdf8", "#7dd3fc"],
  lean:        ["#a78bfa", "#c4b5fd"],
  tossup:      ["#f59e0b", "#fbbf24"],
  unavailable: ["#4b5563", "#6b7280"],
};

let uidCounter = 0;

/**
 * Clean circular SVG gauge ring with gradient arc and
 * understated endpoint indicator. No bloom, no glow.
 */
function GaugeRing({ confidence, tier }: { confidence: number | null; tier: string }): JSX.Element {
  const idRef = React.useRef<string | null>(null);
  if (idRef.current === null) {
    idRef.current = "gr-" + (++uidCounter);
  }
  const uid = idRef.current;

  const pct = confidence !== null && !Number.isNaN(confidence) ? confidence : 0;
  const offset = RING_CIRCUMFERENCE * (1 - pct);
  const [colorMain, colorLight] = TIER_COLORS[tier] || TIER_COLORS.unavailable;

  /* Endpoint dot position (arc starts at 12-o'clock after −90° rotation) */
  const endAngle = -Math.PI / 2 + 2 * Math.PI * pct;
  const dotX = CENTER + RING_RADIUS * Math.cos(endAngle);
  const dotY = CENTER + RING_RADIUS * Math.sin(endAngle);

  return (
    <svg
      className="gauge-ring"
      width={RING_SIZE}
      height={RING_SIZE}
      viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={`${uid}-grad`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={colorLight} stopOpacity="0.9" />
          <stop offset="100%" stopColor={colorMain} stopOpacity="0.8" />
        </linearGradient>
      </defs>

      {/* Subtle background track */}
      <circle
        className="gauge-track"
        cx={CENTER}
        cy={CENTER}
        r={RING_RADIUS}
        fill="none"
        strokeWidth={RING_STROKE}
      />

      {/* Filled arc with gradient stroke */}
      <circle
        className="gauge-fill"
        cx={CENTER}
        cy={CENTER}
        r={RING_RADIUS}
        fill="none"
        stroke={`url(#${uid}-grad)`}
        strokeWidth={RING_STROKE}
        strokeLinecap="round"
        strokeDasharray={RING_CIRCUMFERENCE}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${CENTER} ${CENTER})`}
      />

      {/* Small endpoint dot */}
      {pct > 0.03 && (
        <circle
          cx={dotX}
          cy={dotY}
          r={2.5}
          fill={colorLight}
          opacity={0.85}
        />
      )}
    </svg>
  );
}

export function ConfidenceBadge({ confidence, onClick, expanded }: ConfidenceBadgeProps): JSX.Element {
  const label = formatConfidencePercent(confidence);
  const tier = getConfidenceTier(confidence);

  const className = `confidence-badge confidence-${tier}`;

  if (onClick) {
    return (
      <button
        type="button"
        className={className}
        onClick={onClick}
        aria-expanded={expanded}
        aria-label={`Confidence: ${label}. Click to learn more.`}
        title="Click to learn how confidence works"
      >
        <div className="gauge-wrap">
          <GaugeRing confidence={confidence} tier={tier} />
          <span className="confidence-value">{label}</span>
        </div>
        <span className="confidence-label">Confidence</span>
        <span className="confidence-info-icon" aria-hidden="true">ⓘ</span>
      </button>
    );
  }

  return (
    <span className={className} aria-label={`Confidence: ${label}`}>
      <div className="gauge-wrap">
        <GaugeRing confidence={confidence} tier={tier} />
        <span className="confidence-value">{label}</span>
      </div>
      <span className="confidence-label">Confidence</span>
    </span>
  );
}
